import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { DashboardPanelInterface } from '@models/dashboard-panel-interface';
import { ChannelGroupService } from '@service/channel-groups.service';
import { ChannelService } from '@service/channel.service';
import { CssVarService } from '@service/css-vars.service';
import { CustomGroupsService } from '@service/custom-groups.service';
import { DeviceFlagsService } from '@service/device-flags.service';
import { FitToContainerPaginationStrategy } from '@service/fit-to-container-pagination-strategy';
import { NavigationService } from '@service/navigation.service';
import { ResponsiveService } from '@service/responsive.service';

import { Channel } from 'ops/common/models/channel';
import { ChannelGroup } from 'ops/common/models/channel-group';
import { CustomGroup } from 'ops/common/models/custom-group';

import { lastValueFrom } from 'rxjs';

@Component({
    selector: 'app-dashboard-panel-channel-group',
    templateUrl: './dashboard-panel-channel-group.component.html',
    styleUrls: ['./dashboard-panel-channel-group.component.scss'],
    providers: [FitToContainerPaginationStrategy],
    standalone: false,
})
export class DashboardPanelChannelGroupComponent implements DashboardPanelInterface, OnInit {
    @ViewChild('tableBody', { static: true })
    public tableBodyContainer: ElementRef;

    @Output() onDeleteClicked = new EventEmitter<void>();
    @Output() onResourceNotFound = new EventEmitter<void>();
    @Output() onGoToPageClicked = new EventEmitter<void>();
    @Input() groupId = '';
    @Input() isPreview = false;

    isLoading = true;
    currentPage = 0;

    channels: Channel[] = [];
    group: ChannelGroup | CustomGroup;
    isCustomGroup: boolean = false;

    constructor(
        private channelGroupService: ChannelGroupService,
        private customGroupsService: CustomGroupsService,
        private channelService: ChannelService,
        private paginationStrategy: FitToContainerPaginationStrategy,
        private navigationService: NavigationService,
        private cssVarService: CssVarService,
        public responsive: ResponsiveService,
        private deviceFlags: DeviceFlagsService,
    ) {}

    async ngOnInit(): Promise<void> {
        this.paginationStrategy.init(
            this.tableBodyContainer,
            this.cssVarService.tableRowHeightChannel,
            this.cssVarService.tableRowGap,
        );

        let [groups, channels] = await Promise.all([
            lastValueFrom(this.channelGroupService.getGroups()),
            lastValueFrom(this.channelService.getChannels()),
        ]);
        let group = groups.find((g) => g.id === this.groupId);

        if (group) {
            this.channels = channels.filter((c) => c.groupId === group.id);
            this.channels = this.channels.sort((a, b)=> a.rowIndex - b.rowIndex);
            this.group = group;
        } else {
            let customGroups = await this.customGroupsService.getGroups();
            let customGroup = customGroups.find((g) => g.id === this.groupId);

            if (customGroup) {
                let groupChannels = await this.customGroupsService.getAllChannelsCustomGroups();
                let channelIds = groupChannels
                    .filter((groupChannel) => groupChannel.customGroupId === customGroup.id)
                    .map((groupChannel) => groupChannel.channelId);
                let channelIdSet = new Set(channelIds);

                this.channels = channels.filter((channel) => channelIdSet.has(channel.id));
                this.group = customGroup;
                this.isCustomGroup = true;
            } else {
                this.emitResourceNotFound();
            }
        }

        this.paginationStrategy.recalculate(this.channels.length);
        this.isLoading = false;
    }

    @HostListener('window:resize')
    handleWindowResize() {
        this.paginationStrategy.init(
            this.tableBodyContainer,
            this.cssVarService.tableRowHeightChannel,
            this.cssVarService.tableRowGap,
        );
        this.paginationStrategy.recalculate(this.channels.length);
    }

    handlePageChanged(pageNumber: number) {
        this.currentPage = pageNumber;
    }

    handleGoToPageClicked() {
        if (this.isPreview) {
            this.onGoToPageClicked.emit();
        } else {
            if (this.isCustomGroup) {
                this.navigationService.toCustomChannelGroup(this.group);
            } else {
                this.navigationService.toChannelGroup(this.group);
            }
        }
    }

    handleDeleteClicked() {
        this.emitDeleteClicked();
    }

    handleChannelSelected(channel: Channel) {
        this.navigationService.toChannelDetails(channel, this.group);
    }

    emitDeleteClicked() {
        this.onDeleteClicked.emit();
    }

    emitResourceNotFound() {
        this.onResourceNotFound.emit();
    }

    public get itemsPerPage() {
        return this.paginationStrategy.getItemsPerPage();
    }
    public get pageStart() {
        return this.paginationStrategy.getPageStart(this.currentPage);
    }
    public get pageEnd() {
        return this.paginationStrategy.getPageEnd(this.currentPage);
    }
    public get pageCount() {
        return this.paginationStrategy.getPageCount();
    }
    public get title() {
        return this.group?.displayName;
    }

    public displayedPageNumber() {
        if (this.responsive.isLarge()) {
            return 3;
        } else if (this.responsive.isXSmall() || this.responsive.isSmall()) {
            return 1;
        } else {
            return 5;
        }
    }
}
